/**
 * Home — the study dashboard: continue-where-you-left-off, at-a-glance stats,
 * per-test readiness, and a single suggested next step. Reads progress through
 * the context, so a new user sees honest empty states (zeros, "start your first
 * lesson"). Ports the design's renderHome.
 */
import { useNavigate } from 'react-router-dom';
import { lessons, flashcards, tests, TEST_KEYS } from '@/content/training';
import { useTrainingProgress } from '../TrainingProgressContext';
import { trainingPath } from '../trainingNav';
import { PageHeader, Card, Button, ProgressBar } from './TrainingUI';

export function Home() {
  const navigate = useNavigate();
  const progress = useTrainingProgress();

  const lessonsTotal = lessons.length;
  const lessonsDone = Object.keys(progress.lessonsDone).length;
  const cardsKnown = Object.keys(progress.cardKnown).length;
  const missed = progress.missedList().length;
  const exams = progress.examHistory.length;
  const examsPassed = progress.examHistory.filter((e) => e.passed).length;

  const nextLesson = lessons.find((l) => !progress.lessonsDone[l.id]);
  const lastLesson =
    progress.lastLesson && !progress.lessonsDone[progress.lastLesson.id]
      ? lessons.find((l) => l.id === progress.lastLesson!.id)
      : undefined;

  // The "continue" card: resume → all-done → up-next.
  let cont: { kicker: string; title: string; note: string; label: string; onClick: () => void };
  if (lastLesson) {
    cont = {
      kicker: 'Continue',
      title: lastLesson.title,
      note: `${tests[lastLesson.test].name} · Lesson ${lastLesson.num}`,
      label: 'Resume lesson',
      onClick: () => navigate(`${trainingPath('learn')}/${lastLesson.id}`),
    };
  } else if (lessonsDone === lessonsTotal) {
    cont = {
      kicker: 'All lessons complete',
      title: 'Prove it on a full General Knowledge exam',
      note: '50 questions, DMV format, no feedback until the end.',
      label: 'Start GK exam',
      onClick: () => navigate(trainingPath('exam')),
    };
  } else {
    const up = nextLesson!;
    cont = {
      kicker: lessonsDone === 0 ? 'Start here' : 'Up next',
      title: up.title,
      note: `${tests[up.test].name} · Lesson ${up.num}`,
      label: lessonsDone === 0 ? 'Start your first lesson' : 'Start lesson',
      onClick: () => navigate(`${trainingPath('learn')}/${up.id}`),
    };
  }

  const tiles = [
    {
      label: 'Lessons completed',
      value: `${lessonsDone} / ${lessonsTotal}`,
      pct: Math.round((100 * lessonsDone) / lessonsTotal),
      note: undefined as string | undefined,
      to: trainingPath('learn'),
    },
    {
      label: 'Flashcards mastered',
      value: `${cardsKnown} / ${flashcards.length}`,
      pct: Math.round((100 * cardsKnown) / flashcards.length),
      note: undefined,
      to: trainingPath('cards'),
    },
    {
      label: 'Exams taken',
      value: String(exams),
      pct: undefined,
      note: exams ? `${examsPassed} passed` : 'Take a full simulation',
      to: trainingPath('exam'),
    },
    {
      label: 'Missed questions',
      value: String(missed),
      pct: undefined,
      note: missed ? 'Drill these in Review' : 'Nothing to review — nice',
      to: trainingPath('review'),
    },
  ];

  // Suggested next step
  let suggestion: { msg: string; label: string; onClick: () => void };
  if (lessonsDone < 4) {
    suggestion = {
      msg: 'Start with the lessons — they teach every fact the questions draw from, in the order the handbook presents them.',
      label: 'Go to Lessons',
      onClick: () => navigate(trainingPath('learn')),
    };
  } else if (exams === 0) {
    suggestion = {
      msg: "You've been studying — see where you stand with a full General Knowledge exam simulation (50 questions, real DMV format).",
      label: 'Take a practice exam',
      onClick: () => navigate(trainingPath('exam')),
    };
  } else if (missed > 0) {
    suggestion = {
      msg: `You have ${missed} missed question${missed > 1 ? 's' : ''} waiting. Clearing your missed list is the fastest way to raise your score.`,
      label: 'Review missed questions',
      onClick: () => navigate(trainingPath('review')),
    };
  } else {
    suggestion = {
      msg: 'Keep your streak going — mix flashcards for the numbers with practice exams until every test sits above 90%.',
      label: 'Flashcards',
      onClick: () => navigate(trainingPath('cards')),
    };
  }

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title="Ready to earn that Class A?"
        sub="Track your readiness for each of the three knowledge tests you'll take at the DMV. Aim for consistent scores above 90% here before test day — the real pass mark is 80%, and you only get 3 attempts per test."
      />

      <Card className="cont-card">
        <div className="cont-k">{cont.kicker}</div>
        <div className="cont-t">{cont.title}</div>
        <div className="muted">{cont.note}</div>
        <Button variant="primary" className="mt-4" onClick={cont.onClick}>
          {cont.label}
        </Button>
      </Card>

      <div className="tile-row mt-4">
        {tiles.map((t) => (
          <button key={t.label} className="tile" onClick={() => navigate(t.to)}>
            <div className="t-label">{t.label}</div>
            <div className="t-value">{t.value}</div>
            {t.pct != null && <ProgressBar pct={t.pct} />}
            {t.note && <div className="t-note">{t.note}</div>}
          </button>
        ))}
      </div>

      <Card className="mt-4">
        <h3 className="">Test readiness</h3>
        {TEST_KEYS.map((k) => {
          const t = tests[k];
          const acc = progress.accuracyFor(k);
          const cov = progress.coverageFor(k);
          return (
            <div key={k} className="test-row">
              <div className="info">
                <div className="name">{t.name}</div>
                <div className="meta">
                  {t.qCount} questions · pass with {t.pass} correct (80%) ·{' '}
                  {acc === null ? 'no practice yet' : `${cov}% of bank seen`}
                </div>
                <ProgressBar pct={acc ?? 0} />
              </div>
              <div className="pct">{acc === null ? '—' : `${acc}%`}</div>
              <Button variant="ghost" className="small" onClick={() => navigate(trainingPath('practice'))}>
                Practice
              </Button>
            </div>
          );
        })}
      </Card>

      <Card className="cont-card mt-4">
        <div className="cont-k">Suggested next step</div>
        <p className="muted mt-2">{suggestion.msg}</p>
        <Button variant="primary" className="mt-4" onClick={suggestion.onClick}>
          {suggestion.label}
        </Button>
      </Card>
    </div>
  );
}
